import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Send, Loader2, Lock, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const SEGMENTS = [
  { id: 'non_confirmees', label: 'Pré-inscriptions non confirmées', desc: 'Les inviter à confirmer leur place.' },
  { id: 'sans_formule', label: 'Confirmées sans formule', desc: 'Les inviter à choisir leur formule.' },
  { id: 'sans_mandat', label: 'Confirmées sans mandat SEPA', desc: 'Lien direct vers la signature du mandat.' },
  { id: 'toujours_puro', label: '« Toujours partie de PURO ? »', desc: 'Bouton confirmer / se désinscrire.' },
];

export default function RelanceCampaignPanel() {
  const [counts, setCounts] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/relances/segments`, { withCredentials: true });
      setCounts(res.data);
    } catch (e) { /* silencieux */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!confirm) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/relances/send`, { segment: confirm.id }, { withCredentials: true });
      const d = res.data;
      if (!d.client_emails_enabled) toast.warning(`Verrou R1 : aucun email envoyé. ${d.blocked} tentative(s) journalisée(s).`, { duration: 7000 });
      else toast.success(`${d.sent} envoyé(s), ${d.blocked} bloqué(s), ${d.skipped} ignoré(s).`);
      setConfirm(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la relance.');
    } finally { setSending(false); }
  };

  const enabled = counts?.client_emails_enabled;

  return (
    <Card className="border border-[#E9DDCC] rounded-2xl bg-white" data-testid="relance-campaign-panel">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <MailCheck className="w-4 h-4 text-[#C99A55]" strokeWidth={1.8} />
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#C99A55] font-semibold">Relances</span>
        </div>
        <h3 className="font-heading text-lg text-[#493329]">Campagnes de relance</h3>

        <div className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm ${enabled ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-[#FFF8E6] text-[#493329] border border-[#C99A55]/40'}`} data-testid="relance-r1-banner">
          <Lock className="w-4 h-4 shrink-0" />
          {enabled
            ? 'Envois clientes ACTIVÉS — les relances partiront réellement.'
            : 'Verrou R1 actif : aucune relance ne part réellement, les tentatives sont journalisées.'}
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          {SEGMENTS.map((s) => (
            <div key={s.id} className="rounded-xl border border-[#E9DDCC] p-4 flex flex-col gap-2" data-testid={`relance-seg-${s.id}`}>
              <div className="flex items-baseline justify-between">
                <span className="font-medium text-[#493329] text-sm">{s.label}</span>
                <span className="font-heading text-xl text-[#C99A55]" data-testid={`relance-count-${s.id}`}>{counts ? counts[s.id] : '—'}</span>
              </div>
              <p className="text-[11px] text-[#AE9885]">{s.desc}</p>
              <Button onClick={() => setConfirm(s)} disabled={!counts || counts[s.id] === 0}
                className="rounded-full bg-[#C76542] hover:bg-[#b2573a] text-white h-9 disabled:opacity-40 mt-auto"
                data-testid={`relance-send-${s.id}`}>
                <Send className="w-4 h-4 mr-1" /> Relancer ({counts ? counts[s.id] : 0})
              </Button>
            </div>
          ))}
        </div>
      </CardContent>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="relance-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#493329]">Confirmer la relance</DialogTitle>
            <DialogDescription>
              Segment : <strong>{confirm?.label}</strong> — {confirm ? counts?.[confirm.id] : 0} destinataire(s).
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 bg-[#FFF8E6] border border-[#C99A55]/40 rounded-xl p-3 text-sm text-[#493329]">
            <Lock className="w-4 h-4 mt-0.5 text-[#9A744C] shrink-0" />
            <span><strong>Règle R1.</strong> {enabled ? 'Les envois clientes sont activés : les emails partiront réellement.' : 'Tant que les envois clientes ne sont pas activés, rien ne part — seules les tentatives sont journalisées.'}</span>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirm(null)} data-testid="relance-cancel">Annuler</Button>
            <Button onClick={send} disabled={sending} className="bg-[#C76542] hover:bg-[#b2573a] text-white" data-testid="relance-confirm-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} OK pour envoi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
